import { bn } from 'shared/utils/bignumber'
import { BigNumber } from 'ethers'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useWithdrawalDepositSubmit } from './useWithdrawalDepositSubmit'
import { useDepositRiskOptions } from 'modules/pools/hooks/useDepositRiskOptions'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import { useIsPoolPaused } from 'modules/pools/hooks/useIsPoolPaused'
import { useAssetsData } from 'modules/pools/hooks/useAssetsData'

import { Text } from 'shared/ui/common/Text'
import { SelectControl } from 'shared/ui/controls/Select'
import { FormSubmitter } from 'shared/ui/common/FormSubmitter'
import { Form } from 'shared/ui/controls/FormNetworkGuarded'
import {
  FormLockedValue,
  FormLockedValuesList,
} from 'shared/ui/common/FormLockedValue'
import {
  FormInfoItem,
  FormInfoFrame,
  FormInfoFramesList,
} from 'shared/ui/common/FormInfoFrame'
import { EstimatedGasFee } from 'modules/blockChain/ui/EstimatedGasFee'

import * as formErrors from 'shared/constants/formErrors'
import {
  poolAssetOptions,
  getPoolAssetIcon,
  PoolAsset,
} from 'modules/pools/constants/poolAssets'
import type { FormValues, SuccessData } from './types'
import { PoolRisk } from 'modules/pools/constants/PoolRisk'
import { weiToStr } from 'modules/blockChain/utils/parseWei'
import { formatAsset } from 'modules/pools/utils/formatAsset'
import type { DepositPoolInfo } from 'modules/pools/utils/calcDepositPoolInfo'
import { getPoolInvestorByAssetAndRisk } from 'modules/pools/utils/getPoolInvestorContract'
import { ContractRevenueManager } from 'modules/contracts/contracts'
import { protocolPaused } from 'shared/constants/errors'

import s from './FormWithdrawalDeposit.module.scss'

const ASSET_OPTIONS = [
  poolAssetOptions.WETH,
  poolAssetOptions.USDC,
  // poolAssetOptions.USDT,
  // poolAssetOptions.DAI,
]

const AMOUNT_STEPS = [10, 25, 50, 75, 100]

type Props = {
  risk: PoolRisk
  asset: PoolAsset
  depositInfo: DepositPoolInfo
  poolAddress: string
  onSuccess: (res: SuccessData) => void
}

export function FormWithdrawalDeposit({
  risk,
  asset,
  depositInfo,
  poolAddress,
  onSuccess,
}: Props) {
  const { chainId, isChainUnsupported } = useWeb3()
  const { debtTokenBalance, debtTokenDecimals, deposit } = depositInfo
  const riskOptions = useDepositRiskOptions(asset)
  const { getAssetDecimals } = useAssetsData()

  const depositedAssetDecimals = getAssetDecimals(asset)

  const debtTokenStr = weiToStr(debtTokenBalance, debtTokenDecimals)

  const { data: balanceAvailable } = ContractRevenueManager.useSwrRpc(
    'balanceAvailable',
    [
      getPoolInvestorByAssetAndRisk(asset, risk).contract.chainAddress.get(
        chainId,
      ),
    ],
  )
  const { data: isPoolInvestorPaused } = useIsPoolPaused({
    risk,
    asset,
  })

  const formMethods = useForm<FormValues>({
    mode: 'all',
    shouldUnregister: false,
    defaultValues: {
      asset,
      amountPercents: '100',
      liquidity: debtTokenStr,
      depositedAmount: weiToStr(deposit, depositedAssetDecimals),
      risk,
    },
  })

  const { getValues, setValue, trigger } = formMethods
  useEffect(() => {
    if (debtTokenStr !== getValues('liquidity')) {
      setValue('liquidity', debtTokenStr)
    }
  }, [debtTokenStr, getValues, setValue])

  const selectedAmountValue = formMethods.watch('amountPercents')

  const exceedsPoolBalance = useMemo(() => {
    return (
      Boolean(balanceAvailable) &&
      deposit
        .mul(BigNumber.from(selectedAmountValue))
        .div(100)
        .gt(balanceAvailable!)
    )
  }, [deposit, selectedAmountValue, balanceAvailable])

  useEffect(() => {
    trigger('amountPercents')
  }, [trigger, exceedsPoolBalance])

  const { submit, isSubmitting, isLocked, estimatedGas, unlockForm } =
    useWithdrawalDepositSubmit({
      poolAddress,
      onSuccess,
      formMethods,
      debtTokenDecimals,
    })

  const amountOptions = useMemo(() => {
    const depositedAmountUSDbn = new bn(
      weiToStr(deposit, depositedAssetDecimals),
    )
    return AMOUNT_STEPS.map(percent => {
      const display = depositedAmountUSDbn
        .times(percent / 100)
        .toFixed(depositedAssetDecimals)
      return {
        display,
        label: (
          <>
            <Text weight={600}>{percent === 100 ? 'MAX' : `${percent}%`}</Text>
            &nbsp;
            <Text color="secondary">
              ({formatAsset(display, asset)} {asset})
            </Text>
          </>
        ),
        value: String(percent),
      }
    })
  }, [asset, deposit, depositedAssetDecimals])

  const depositedAmount = formMethods.watch('depositedAmount')
  const selectedAmountDisplay = useMemo(() => {
    return amountOptions.find(o => o.value === selectedAmountValue)?.display
  }, [amountOptions, selectedAmountValue])

  return (
    <Form formMethods={formMethods} onSubmit={submit}>
      {isPoolInvestorPaused && (
        <Text isCentered color="error" className={s.errorMessage}>
          {protocolPaused}
        </Text>
      )}
      {!isLocked && (
        <>
          <SelectControl
            readOnly
            name="asset"
            placeholder="Asset"
            options={ASSET_OPTIONS}
            rules={{ required: formErrors.required }}
          />

          <SelectControl
            name="amountPercents"
            concat="bottom"
            placeholder="Amount"
            options={amountOptions}
            rules={{
              required: formErrors.required,
              validate: () =>
                exceedsPoolBalance ? formErrors.POOL_BALANCE_ERROR : true,
            }}
            readOnly={isPoolInvestorPaused || isChainUnsupported}
            testId="deposit-withdraw-form-amount-select"
          />

          <SelectControl
            name="risk"
            concat="top"
            placeholder="Risk Pool / APY"
            readOnly
            options={riskOptions}
          />
        </>
      )}

      {isLocked && (
        <FormLockedValuesList>
          <FormLockedValue
            label="Asset"
            name="asset"
            getIcon={getPoolAssetIcon}
          />
          <FormLockedValue
            label="Amount"
            name="amountPercents"
            formatValue={() => formatAsset(selectedAmountDisplay, asset)}
            tooltip={selectedAmountDisplay}
          />
          <FormLockedValue
            label="Target risk pool"
            name="risk"
            valueSize={16}
            formatValue={v => riskOptions.find(o => o.value === v)?.label}
          />
        </FormLockedValuesList>
      )}

      <FormInfoFramesList>
        <FormInfoFrame>
          <FormInfoItem
            label="Deposited amount"
            value={formatAsset(depositedAmount, asset)}
            sign={asset}
            tooltip={depositedAmount}
          />
          {/* <FormInfoItem label="" value="" /> */}
        </FormInfoFrame>
      </FormInfoFramesList>

      {estimatedGas && <EstimatedGasFee value={estimatedGas} />}

      <FormSubmitter
        isLocked={isLocked}
        isSubmitting={isSubmitting}
        isDisabled={isPoolInvestorPaused || isChainUnsupported}
        firstStepText="Withdraw"
        onClickUnlock={unlockForm}
        submitTestId="deposit-withdraw-form-submit-button"
        confirmTestId="deposit-withdraw-form-confirm-button"
      />
    </Form>
  )
}
